import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "./assets/design/index.scss";
import "./i18n";
import store, { type AppDispatch } from "./store";
import { queryClient } from "./api/queryClient";
import reportWebVitals from "./reportWebVitals";
import ErrorPage from "./pages/Error/ErrorPage";
import * as Sentry from "@sentry/react";
import { initSentry } from "./config/sentry";
import Page from "./Page";
import { registerAuthCallbacks } from "./logic/axiosClient";
import { logoutUser, refreshUserInformation } from "./logic/Auth/auth.thunk";
import { localStore } from "./utils/storage";

// Initialise Sentry before anything else so any boot-time error (a
// missing env var, a thrown reducer initialiser) is still captured.
// No-ops in dev / tests when the DSN is empty.
initSentry();

// The raw `store.dispatch` typed at module level resolves to
// `Dispatch<UnknownAction>` only (RTK 2 quirk; see comment in store.ts).
// We re-type it here so the dispatches below can take thunks.
const dispatch = store.dispatch as AppDispatch;

// Wire the axios interceptor to the auth thunks once, at app boot.
// axiosClient itself does not import the store — it accepts callbacks.
registerAuthCallbacks({
  onRefreshNeeded: async () => {
    await dispatch(refreshUserInformation()).unwrap();
  },
  onUnauthorized: () => {
    // Contract: a session that was live when the refresh failed needs
    // the full logout teardown (server /auth/logout, push unsubscribe,
    // query-cache wipe). An anonymous cold boot doesn't — the /me
    // rejection already normalises the slice, and dispatching logout
    // would fire a pointless /auth/logout on every anonymous visit.
    // This gate only works because `refreshUserInformation.rejected`
    // no longer de-auths (single-writer — see authSlice.ts): the state
    // read here is still the pre-failure auth state.
    if (store.getState().auth.isAuthenticated) {
      dispatch(logoutUser());
    }
  },
  // The refresh token is an httpOnly cookie (unreadable from JS), so we
  // gate the refresh on the app's auth state instead: only skip when
  // boot has finished AND we're not authenticated (i.e. definitively
  // logged out — no session to refresh). While still bootstrapping we
  // allow the attempt so a valid session restores on a cold reload —
  // UNLESS this browser is KNOWN to have no session ("br-had-session"
  // === false, written on logout / server-rejected refresh): that skips
  // the doomed /auth/refresh round-trip on anonymous visits. Absent hint
  // = unknown → fail-open and attempt, so pre-existing sessions restore.
  shouldAttemptRefresh: () => {
    const { isAuthenticated, bootStatus } = store.getState().auth;
    if (isAuthenticated) return true;
    return bootStatus !== "ready" && localStore.get("br-had-session") !== false;
  },
});

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
  <Sentry.ErrorBoundary fallback={ErrorPage}>
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Page />
      </Provider>
      {/*
        Devtools render only when import.meta.env.DEV is true. Vite
        evaluates this at build time so the devtools chunk is fully
        tree-shaken out of production bundles.
      */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </QueryClientProvider>
  </Sentry.ErrorBoundary>,
);

reportWebVitals();
